import { useMemo, useState, useEffect } from "react";
import { useInfiniteListSavedSearches } from "../hooks/useInfiniteListSavedSearches";
import { useSavedSearches } from "../hooks/useSavedSearches";
import ComboBox, { ComboBoxListOption } from "src/components/core/ComboBox";
import { useQueryClient } from "@tanstack/react-query";
import { SavedSearch } from "src/types/neighborhoodSearch";
import { isRegionSearch } from "../utils/isRegionSearch";
import { NS_SAVED_SEARCH_CACHE_KEY } from "../constants/cacheKeys";

export const SavedSearchSelector = ({
    selectedItem,
    onSelect,
    onSelectedSearchChange,
    pageSize = 20,
    disableNonRegionSearches = false,
}: {
    selectedItem: ComboBoxListOption | null | undefined;
    onSelect: (selected: ComboBoxListOption | null | undefined) => void;
    onSelectedSearchChange?: (selectedSearch: SavedSearch | null) => void;
    pageSize?: number;
    disableNonRegionSearches?: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const queryClient = useQueryClient();

  const {
    data: savedSearchesPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteListSavedSearches({
    search: searchTerm,
  });
  
  // Get selected ID
  const selectedId = selectedItem?.value;

  const { data: selectedSearches } = useSavedSearches(selectedId ? [selectedId] : []);

  // Call onSelectedSearchChange when selectedSearch changes
  useEffect(() => {
    const selectedSearch = selectedSearches?.[0] || null;
    if (onSelectedSearchChange) {
      onSelectedSearchChange(selectedSearch);
    }
  }, [selectedSearches, onSelectedSearchChange]);

  const savedSearchesOptions = useMemo(() => {
    const serverItems = savedSearchesPages?.pages.flatMap((page) =>
      page.map((search) => ({
        label: search.name,
        value: search.id,
        disabled: disableNonRegionSearches ? !isRegionSearch(search) : false,
      } as ComboBoxListOption))
    ) || [];

    // Add the selected item if it's not in the main list
    if (selectedItem && !serverItems.some(item => item.value === selectedItem.value)) {
      const selectedSearch = selectedSearches?.[0];
      if (selectedSearch) {
        return [{
          label: selectedSearch.name,
          value: selectedSearch.id,
          disabled: disableNonRegionSearches ? !isRegionSearch(selectedSearch) : false,
        }, ...serverItems];
      }
    }

    return serverItems;
  }, [selectedSearches, savedSearchesPages, selectedItem, disableNonRegionSearches]);

  const handleSelect = (item: ComboBoxListOption | null | undefined) => {
    onSelect(item);
    // Invalidate the saved search queries to trigger a refetch
    queryClient.invalidateQueries({
      queryKey: [NS_SAVED_SEARCH_CACHE_KEY],
    });
  };

  // Handle search input changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  // Load more when there are more results
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && savedSearchesOptions.length < 50) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, savedSearchesOptions.length]);

  return (
    <ComboBox
      list={savedSearchesOptions}
      value={selectedItem}
      onSelect={handleSelect}
      placeholder="Select a saved search"
      inputValue={inputValue}
      setInputValue={setInputValue}
      shouldFilter={false} // We're doing server-side filtering
      className="w-full"
      buttonClassName="w-full"
    />
  );
};